import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Flame, Users, Calendar, Trophy, Check, Loader2, ArrowLeft, Heart, Upload, Award } from "lucide-react";
import api, { fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Loader, EmptyState, PassionBadge, PrimaryButton, GhostButton, niceDate } from "@/components/ui-bits";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ImageUpload from "@/components/ImageUpload";
import { toast } from "sonner";

function SubmitDialog({ challengeId, onDone }) {
  const [open, setOpen] = useState(false);
  const [caption, setCaption] = useState("");
  const [image, setImage] = useState("");
  const [busy, setBusy] = useState(false);
  const submit = async () => {
    if (!caption.trim()) { toast.error("Add a caption"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/challenges/${challengeId}/submit`, { caption: caption.trim(), image });
      toast.success("Submission posted! 🎉");
      setOpen(false); setCaption(""); setImage(""); onDone(data);
    } catch { toast.error("Could not submit"); } finally { setBusy(false); }
  };
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild><PrimaryButton data-testid="open-submit-dialog"><Upload className="h-4 w-4" />Submit entry</PrimaryButton></DialogTrigger>
      <DialogContent className="rounded-2xl" data-testid="submit-dialog">
        <DialogHeader><DialogTitle>Submit your entry</DialogTitle></DialogHeader>
        <ImageUpload value={image} onChange={setImage} aspect="aspect-video" testid="submission-image" />
        <textarea value={caption} onChange={(e) => setCaption(e.target.value)} rows={3} placeholder="Tell us about your submission…" data-testid="submission-caption"
          className="w-full mt-3 rounded-xl border border-[#071126]/12 p-3 text-sm outline-none focus:border-[#FF6B00]/50 resize-none" />
        <PrimaryButton onClick={submit} disabled={busy} className="w-full mt-2" data-testid="submit-entry-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Post submission"}</PrimaryButton>
      </DialogContent>
    </Dialog>
  );
}

function SubmissionCard({ s, rank, onVote }) {
  return (
    <div className="pa-card p-4" data-testid={`submission-${s.id}`}>
      <div className="flex items-center gap-2 mb-3">
        {rank <= 3 && <span className={`h-6 w-6 rounded-full grid place-items-center text-xs font-bold text-white ${rank === 1 ? "bg-[#FFC94A] text-[#071126]" : rank === 2 ? "bg-[#55708F]" : "bg-[#8FAF9B]"}`}>{rank}</span>}
        <img src={fileUrl(s.author?.avatar)} alt="" className="h-8 w-8 rounded-full object-cover" />
        <span className="text-sm font-semibold text-[#071126]">{s.author?.name}</span>
      </div>
      {s.image && <img src={fileUrl(s.image)} alt="" className="rounded-xl w-full max-h-72 object-cover mb-3" />}
      <p className="text-sm text-[#071126]/80">{s.caption}</p>
      <button onClick={() => onVote(s)} className={`mt-3 inline-flex items-center gap-1.5 text-sm font-medium ${s.voted ? "text-[#FF6B00]" : "text-[#55708F]"} hover:text-[#FF6B00]`} data-testid={`vote-${s.id}`}>
        <Heart className={`h-4 w-4 ${s.voted ? "fill-[#FF6B00]" : ""}`} />{s.vote_count} votes
      </button>
    </div>
  );
}

export default function ChallengeDetail() {
  const { id } = useParams();
  const [c, setC] = useState(null);
  const [subs, setSubs] = useState(null);
  const [busy, setBusy] = useState(false);
  const [tab, setTab] = useState("about");
  const load = () => api.get(`/challenges/${id}`).then((r) => setC(r.data));
  const loadSubs = () => api.get(`/challenges/${id}/submissions`).then((r) => setSubs(r.data));
  useEffect(() => { setC(null); setSubs(null); load(); }, [id]);
  useEffect(() => { if ((tab === "submissions" || tab === "leaderboard") && !subs) loadSubs(); }, [tab]); // eslint-disable-line

  const join = async () => {
    setBusy(true);
    try { await api.post(`/challenges/${id}/join`); toast.success("You're in! Let's go 🔥"); await load(); }
    catch { toast.error("Action failed"); } finally { setBusy(false); }
  };
  const vote = async (s) => {
    const { data } = await api.post(`/submissions/${s.id}/vote`);
    setSubs((ss) => ss.map((x) => x.id === s.id ? { ...x, voted: data.voted, vote_count: data.vote_count } : x).sort((a, b) => b.vote_count - a.vote_count));
  };

  if (!c) return <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8"><Loader /></div>;

  return (
    <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10">
      <Link to="/challenges" className="inline-flex items-center gap-1.5 text-sm text-[#55708F] hover:text-[#FF6B00] mb-5" data-testid="back-to-challenges"><ArrowLeft className="h-4 w-4" />All challenges</Link>
      <div className="rounded-3xl overflow-hidden h-56 sm:h-72 relative">
        <img src={fileUrl(c.cover)} alt={c.title} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#071126]/80 to-transparent" />
        <div className="absolute bottom-5 left-5 right-5 text-white">
          <span className="inline-flex items-center gap-1 text-xs font-semibold px-3 py-1 rounded-full bg-[#FF6B00]"><Flame className="h-3.5 w-3.5" />{c.duration}</span>
          <h1 className="text-3xl sm:text-4xl font-extrabold tracking-tight mt-2">{c.title}</h1>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-8 mt-8">
        <div className="lg:col-span-2">
          <Tabs value={tab} onValueChange={setTab}>
            <TabsList className="h-auto p-1 bg-[#071126]/5 rounded-xl mb-5">
              <TabsTrigger value="about" data-testid="challenge-tab-about" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#FF6B00] px-4 py-2 text-sm font-medium">About</TabsTrigger>
              <TabsTrigger value="submissions" data-testid="challenge-tab-submissions" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#FF6B00] px-4 py-2 text-sm font-medium">Submissions</TabsTrigger>
              <TabsTrigger value="leaderboard" data-testid="challenge-tab-leaderboard" className="rounded-lg data-[state=active]:bg-white data-[state=active]:text-[#FF6B00] px-4 py-2 text-sm font-medium">Leaderboard</TabsTrigger>
            </TabsList>
            <TabsContent value="about" className="space-y-5">
              <PassionBadge slug={c.passion} />
              <p className="text-[#071126]/70 leading-relaxed whitespace-pre-wrap">{c.description}</p>
              {c.rules?.length > 0 && <div className="pa-card p-5"><h3 className="font-semibold mb-3">Rules</h3><ul className="space-y-2">{c.rules.map((r, i) => <li key={i} className="flex gap-2.5 text-sm text-[#071126]/70"><Check className="h-4 w-4 text-[#8FAF9B] flex-shrink-0 mt-0.5" />{r}</li>)}</ul></div>}
              {c.reward && <div className="pa-card p-5 flex items-start gap-3"><span className="h-10 w-10 rounded-xl bg-[#FFC94A]/25 grid place-items-center flex-shrink-0"><Award className="h-5 w-5 text-[#071126]" /></span><div><p className="font-semibold text-[#071126]">Reward</p><p className="text-sm text-[#071126]/70">{c.reward}</p></div></div>}
            </TabsContent>
            <TabsContent value="submissions">
              {!subs ? <Loader /> : subs.length ? <div className="grid gap-4 sm:grid-cols-2">{subs.map((s, i) => <SubmissionCard key={s.id} s={s} rank={i + 1} onVote={vote} />)}</div>
                : <EmptyState icon={Upload} title="No submissions yet" hint="Be the first to submit your entry." />}
            </TabsContent>
            <TabsContent value="leaderboard">
              {!subs ? <Loader /> : subs.length ? (
                <div className="pa-card divide-y divide-[#071126]/6">
                  {subs.map((s, i) => (
                    <div key={s.id} className="flex items-center gap-3 p-4" data-testid={`leaderboard-row-${i}`}>
                      <span className={`h-7 w-7 rounded-full grid place-items-center text-xs font-bold ${i === 0 ? "bg-[#FFC94A] text-[#071126]" : i === 1 ? "bg-[#55708F] text-white" : i === 2 ? "bg-[#8FAF9B] text-white" : "bg-[#071126]/8 text-[#071126]/60"}`}>{i + 1}</span>
                      <img src={fileUrl(s.author?.avatar)} alt="" className="h-9 w-9 rounded-full object-cover" />
                      <span className="font-semibold text-[#071126] flex-1 truncate">{s.author?.name}</span>
                      <span className="inline-flex items-center gap-1 text-sm text-[#FF6B00] font-semibold"><Heart className="h-4 w-4 fill-[#FF6B00]" />{s.vote_count}</span>
                    </div>
                  ))}
                </div>
              ) : <EmptyState icon={Trophy} title="Leaderboard is empty" hint="Submissions will appear here as people participate." />}
            </TabsContent>
          </Tabs>
        </div>

        <div>
          <div className="pa-card p-6 sticky top-20">
            <div className="space-y-3 text-sm">
              <Detail icon={Users} label={`${c.participant_count} participants`} />
              <Detail icon={Upload} label={`${c.submission_count} submissions`} />
              <Detail icon={Calendar} label={`${niceDate(c.start_date)} – ${niceDate(c.end_date)}`} />
            </div>
            <div className="flex items-center gap-3 mt-5 pt-5 border-t border-[#071126]/8">
              <img src={fileUrl(c.organizer?.avatar)} alt="" className="h-10 w-10 rounded-full object-cover" />
              <div><p className="text-xs text-[#55708F]">Hosted by</p><Link to={`/u/${c.organizer?.id}`} className="font-semibold text-[#071126] hover:text-[#FF6B00] text-sm">{c.organizer?.name}</Link></div>
            </div>
            <div className="mt-5 space-y-2">
              {c.is_joined ? (
                <>
                  <div className="flex items-center justify-center gap-2 text-sm font-semibold text-[#8FAF9B] py-2"><Check className="h-4 w-4" />You've joined</div>
                  <SubmitDialog challengeId={id} onDone={() => { loadSubs(); load(); }} />
                </>
              ) : (
                <PrimaryButton onClick={join} disabled={busy} className="w-full" data-testid="join-challenge-btn">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Flame className="h-4 w-4" />Join challenge</>}</PrimaryButton>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function Detail({ icon: Icon, label }) {
  return <div className="flex items-center gap-3 text-[#071126]/75"><Icon className="h-4.5 w-4.5 text-[#55708F] flex-shrink-0" /><span>{label}</span></div>;
}
